// src/components/profile-input/steps/FamilyHealthStep.jsx
import React, { useState, useEffect, useRef } from "react";
import { Grid, Typography, Divider, TextField } from "@mui/material";
import { TextInput } from "../FormFields";
import { options } from "../formUtils";

const FamilyHealthStep = ({ 
  formData, 
  handleInputChange,
  errors = {}
}) => {
  // State for smart dropdown fields
  const [familyTypeInput, setFamilyTypeInput] = useState("");
  const [showFamilyTypeOptions, setShowFamilyTypeOptions] = useState(false);
  
  const [familyStatusInput, setFamilyStatusInput] = useState("");
  const [showFamilyStatusOptions, setShowFamilyStatusOptions] = useState(false);
  
  const [disabilityInput, setDisabilityInput] = useState("");
  const [showDisabilityOptions, setShowDisabilityOptions] = useState(false);
  
  const [dietInput, setDietInput] = useState("");
  const [showDietOptions, setShowDietOptions] = useState(false);
  
  const [smokingInput, setSmokingInput] = useState("");
  const [showSmokingOptions, setShowSmokingOptions] = useState(false);
  
  const [drinkingInput, setDrinkingInput] = useState("");
  const [showDrinkingOptions, setShowDrinkingOptions] = useState(false);

  const [bloodGroupInput, setBloodGroupInput] = useState("");
  const [showBloodGroupOptions, setShowBloodGroupOptions] = useState(false);

  const [hobbyInput, setHobbyInput] = useState("");
  const [sportInput, setSportInput] = useState("");

  // Refs for dropdown containers to handle click outside
  const familyTypeRef = useRef(null);
  const familyStatusRef = useRef(null);
  const disabilityRef = useRef(null);
  const dietRef = useRef(null);
  const smokingRef = useRef(null);
  const drinkingRef = useRef(null);
  const bloodGroupRef = useRef(null);

  // Initialize from form data
  useEffect(() => {
    setFamilyTypeInput(formData.family?.family_type || "");
    setFamilyStatusInput(formData.family?.family_status || "");
    setDisabilityInput(formData.health?.disability || "");
    setDietInput(formData.habits_and_lifestyle?.diet || "");
    setSmokingInput(formData.health?.smoking || "");
    setDrinkingInput(formData.health?.drinking || "");
    setBloodGroupInput(formData.health?.blood_group || "");
  }, [
    formData.family?.family_type,
    formData.family?.family_status,
    formData.health?.disability,
    formData.habits_and_lifestyle?.diet,
    formData.health?.smoking,
    formData.health?.drinking,
    formData.health?.blood_group
  ]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdowns = [
        { ref: familyTypeRef, setter: setShowFamilyTypeOptions },
        { ref: familyStatusRef, setter: setShowFamilyStatusOptions },
        { ref: disabilityRef, setter: setShowDisabilityOptions },
        { ref: dietRef, setter: setShowDietOptions },
        { ref: smokingRef, setter: setShowSmokingOptions },
        { ref: drinkingRef, setter: setShowDrinkingOptions },
        { ref: bloodGroupRef, setter: setShowBloodGroupOptions }
      ];

      dropdowns.forEach(({ ref, setter }) => {
        if (ref.current && !ref.current.contains(event.target)) {
          setter(false);
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle hobby input change
  const handleHobbyInputChange = (e) => {
    setHobbyInput(e.target.value);
  };

  // Handle sport input change
  const handleSportInputChange = (e) => {
    setSportInput(e.target.value);
  };

  // Handle hobby add
  const handleAddHobby = () => {
    if (hobbyInput.trim()) {
      const currentHobbies = formData.habits_and_lifestyle?.hobbies || [];
      handleInputChange("habits_and_lifestyle.hobbies", [...currentHobbies, hobbyInput.trim()]);
      setHobbyInput("");
    }
  };

  // Handle sport add
  const handleAddSport = () => {
    if (sportInput.trim()) {
      const currentSports = formData.habits_and_lifestyle?.sports_interest || [];
      handleInputChange("habits_and_lifestyle.sports_interest", [...currentSports, sportInput.trim()]);
      setSportInput("");
    }
  };

  // Handle hobby remove
  const handleRemoveHobby = (index) => {
    const currentHobbies = formData.habits_and_lifestyle?.hobbies || [];
    const newHobbies = currentHobbies.filter((_, i) => i !== index);
    handleInputChange("habits_and_lifestyle.hobbies", newHobbies);
  };

  // Handle sport remove
  const handleRemoveSport = (index) => {
    const currentSports = formData.habits_and_lifestyle?.sports_interest || [];
    const newSports = currentSports.filter((_, i) => i !== index);
    handleInputChange("habits_and_lifestyle.sports_interest", newSports);
  };

  // ========== SMART DROPDOWN HANDLERS ==========
  
  // Generic handler for smart dropdowns
  const createSmartDropdownHandlers = (field, setInputFn, setShowOptionsFn) => ({
    handleInputChange: (e) => {
      const value = e.target.value;
      setInputFn(value);
      setShowOptionsFn(true);
      handleInputChange(field, value);
    },
    handleFocus: () => {
      setShowOptionsFn(true);
    },
    handleSelect: (value) => {
      setInputFn(value);
      handleInputChange(field, value);
      setShowOptionsFn(false);
    }
  });

  // Family type handlers
  const familyTypeHandlers = createSmartDropdownHandlers(
    "family.family_type",
    setFamilyTypeInput,
    setShowFamilyTypeOptions
  );

  // Family status handlers
  const familyStatusHandlers = createSmartDropdownHandlers(
    "family.family_status",
    setFamilyStatusInput,
    setShowFamilyStatusOptions
  );

  // Disability handlers
  const disabilityHandlers = createSmartDropdownHandlers(
    "health.disability",
    setDisabilityInput,
    setShowDisabilityOptions
  );

  // Diet handlers
  const dietHandlers = createSmartDropdownHandlers(
    "habits_and_lifestyle.diet",
    setDietInput,
    setShowDietOptions
  );

  // Smoking handlers
  const smokingHandlers = createSmartDropdownHandlers(
    "health.smoking",
    setSmokingInput,
    setShowSmokingOptions
  );

  // Drinking handlers
  const drinkingHandlers = createSmartDropdownHandlers(
    "health.drinking",
    setDrinkingInput,
    setShowDrinkingOptions
  );

  // Blood Group handlers
  const bloodGroupHandlers = createSmartDropdownHandlers(
    "health.blood_group",
    setBloodGroupInput,
    setShowBloodGroupOptions
  );

  // ========== FILTER OPTIONS ==========
  const filteredFamilyTypeOptions = options.family_types.filter(option =>
    option.toLowerCase().includes(familyTypeInput.toLowerCase())
  );

  const filteredFamilyStatusOptions = options.family_statuses.filter(option =>
    option.toLowerCase().includes(familyStatusInput.toLowerCase())
  );

  const filteredDisabilityOptions = options.disabilities.filter(option =>
    option.toLowerCase().includes(disabilityInput.toLowerCase())
  );

  const filteredDietOptions = options.dietary_preferences.filter(option =>
    option.toLowerCase().includes(dietInput.toLowerCase())
  );

  const filteredSmokingOptions = options.yes_no.filter(option =>
    option.toLowerCase().includes(smokingInput.toLowerCase())
  );

  const filteredDrinkingOptions = options.yes_no.filter(option =>
    option.toLowerCase().includes(drinkingInput.toLowerCase())
  );

  const filteredBloodGroupOptions = options.blood_groups.filter(option =>
    option.toLowerCase().includes(bloodGroupInput.toLowerCase())
  );

  // ========== RENDER SMART DROPDOWN COMPONENT ==========
  const renderSmartDropdown = (label, value, handlers, filteredOptions, showOptions, setShowOptionsFn, containerRef, placeholder = "Type or select from options", error = false, helperText = "", required = false) => (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <TextInput
        label={label}
        value={value}
        onChange={handlers.handleInputChange}
        onFocus={handlers.handleFocus}
        placeholder={placeholder}
        fullWidth
        error={error}
        helperText={helperText}
        required={required}
      />
      {showOptions && filteredOptions.length > 0 && (
        <div 
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto',
            marginTop: '2px'
          }}
        >
          {filteredOptions.map((option, index) => (
            <div
              key={`${option}-${index}`}
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                fontSize: '14px'
              }}
              onClick={() => handlers.handleSelect(option)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Calculate total siblings
  const calculateTotalSiblings = () => {
    const brothersTotal = parseInt(formData.family?.siblings?.brothers?.total) || 0;
    const sistersTotal = parseInt(formData.family?.siblings?.sisters?.total) || 0;
    return brothersTotal + sistersTotal;
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary">
          Family Details
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>
      
      {/* Family Type Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Family Type",
          familyTypeInput,
          familyTypeHandlers,
          filteredFamilyTypeOptions,
          showFamilyTypeOptions,
          setShowFamilyTypeOptions,
          familyTypeRef,
          "Type or select family type"
        )}
      </Grid>
      
      {/* Family Status Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Family Status",
          familyStatusInput,
          familyStatusHandlers,
          filteredFamilyStatusOptions,
          showFamilyStatusOptions,
          setShowFamilyStatusOptions,
          familyStatusRef,
          "Type or select family status"
        )}
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Father's Profession"
          value={formData.family?.father_profession || ""}
          onChange={(e) => handleInputChange("family.father_profession", e.target.value)}
          fullWidth
        />
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Mother's Profession"
          value={formData.family?.mother_profession || ""}
          onChange={(e) => handleInputChange("family.mother_profession", e.target.value)}
          fullWidth
        />
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Siblings Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      
      {/* Siblings Grid Container with proper alignment */}
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6}>
            <TextInput
              label="Total Brothers"
              value={formData.family?.siblings?.brothers?.total || ""}
              onChange={(e) => handleInputChange("family.siblings.brothers.total", parseInt(e.target.value) || 0)}
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextInput
              label="Married Brothers"
              value={formData.family?.siblings?.brothers?.married || ""}
              onChange={(e) => handleInputChange("family.siblings.brothers.married", parseInt(e.target.value) || 0)}
              type="number"
              InputProps={{ 
                inputProps: { 
                  min: 0, 
                  max: formData.family?.siblings?.brothers?.total || 0 
                } 
              }}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextInput
              label="Total Sisters"
              value={formData.family?.siblings?.sisters?.total || ""}
              onChange={(e) => handleInputChange("family.siblings.sisters.total", parseInt(e.target.value) || 0)}
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextInput
              label="Married Sisters"
              value={formData.family?.siblings?.sisters?.married || ""}
              onChange={(e) => handleInputChange("family.siblings.sisters.married", parseInt(e.target.value) || 0)}
              type="number"
              InputProps={{ 
                inputProps: { 
                  min: 0, 
                  max: formData.family?.siblings?.sisters?.total || 0 
                } 
              }}
              fullWidth
            />
          </Grid>
          
          {/* Total Siblings - Read Only Field */}
          <Grid item xs={12} style={{ marginTop: '8px' }}>
            <TextField
              fullWidth
              label="Total Siblings (Auto-generated)"
              type="number"
              value={calculateTotalSiblings()}
              InputProps={{ 
                readOnly: true,
                sx: {
                  '& .MuiInputBase-input': {
                    color: 'rgba(0, 0, 0, 0.6)', // Grey color to indicate it's read-only
                    backgroundColor: 'rgba(0, 0, 0, 0.04)', // Light grey background
                  }
                }
              }}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
          Health Information
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>
      
      {/* Blood Group Field - REQUIRED but not compulsory */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Blood Group",
          bloodGroupInput,
          bloodGroupHandlers,
          filteredBloodGroupOptions,
          showBloodGroupOptions,
          setShowBloodGroupOptions,
          bloodGroupRef,
          "Type or select blood group",
          !!errors.blood_group,
          errors.blood_group,
          false  // Changed to false - required indicator but not compulsory
        )}
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, ml: 1 }}>
          Recommended but optional
        </Typography>
      </Grid>
      
      {/* Disability Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Disability",
          disabilityInput,
          disabilityHandlers,
          filteredDisabilityOptions,
          showDisabilityOptions,
          setShowDisabilityOptions,
          disabilityRef,
          "Type or select disability"
        )}
      </Grid>
      
      {/* Medical Conditions Field */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Medical Conditions"
          value={formData.health?.medical_conditions || ""}
          onChange={(e) => handleInputChange("health.medical_conditions", e.target.value)}
          fullWidth
          placeholder="Any known medical conditions"
        />
      </Grid>
      
      {/* Diet Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Diet",
          dietInput,
          dietHandlers,
          filteredDietOptions,
          showDietOptions,
          setShowDietOptions,
          dietRef,
          "Type or select diet"
        )}
      </Grid>
      
      {/* Smoking Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Smoking *",
          smokingInput,
          smokingHandlers,
          filteredSmokingOptions,
          showSmokingOptions,
          setShowSmokingOptions,
          smokingRef,
          "Type or select smoking habit"
        )}
      </Grid>
      
      {/* Drinking Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Drinking *",
          drinkingInput,
          drinkingHandlers,
          filteredDrinkingOptions,
          showDrinkingOptions,
          setShowDrinkingOptions,
          drinkingRef,
          "Type or select drinking habit"
        )}
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Hobbies & Interests
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      
      {/* Hobbies - Custom Array Input */}
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={9}>
            <TextInput
              label="Add Hobby"
              value={hobbyInput}
              onChange={handleHobbyInputChange}
              onKeyPress={(e) => e.key === 'Enter' && handleAddHobby()}
              fullWidth
              placeholder="Type a hobby and press Add or Enter"
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <button
              onClick={handleAddHobby}
              disabled={!hobbyInput.trim()}
              style={{
                padding: '8px 16px',
                backgroundColor: hobbyInput.trim() ? '#1976d2' : '#cccccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: hobbyInput.trim() ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                width: '100%',
                height: '56px',
                marginTop: '8px'
              }}
            >
              Add
            </button>
          </Grid>
        </Grid>

        {/* Display added hobbies as chips */}
        {formData.habits_and_lifestyle?.hobbies && formData.habits_and_lifestyle.hobbies.length > 0 && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {formData.habits_and_lifestyle.hobbies.map((hobby, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    padding: '6px 12px',
                    borderRadius: '16px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>{hobby}</span>
                  <button
                    onClick={() => handleRemoveHobby(index)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#f44336',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '0',
                      lineHeight: '1'
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </Grid>
        )}
      </Grid>
      
      {/* Sports Interest - Custom Array Input */}
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={9}>
            <TextInput
              label="Add Sports Interest"
              value={sportInput}
              onChange={handleSportInputChange}
              onKeyPress={(e) => e.key === 'Enter' && handleAddSport()}
              fullWidth
              placeholder="Type a sports interest and press Add or Enter"
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <button
              onClick={handleAddSport}
              disabled={!sportInput.trim()}
              style={{
                padding: '8px 16px',
                backgroundColor: sportInput.trim() ? '#1976d2' : '#cccccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: sportInput.trim() ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                width: '100%',
                height: '56px',
                marginTop: '8px'
              }}
            >
              Add
            </button>
          </Grid>
        </Grid>

        {/* Display added sports as chips */}
        {formData.habits_and_lifestyle?.sports_interest && formData.habits_and_lifestyle.sports_interest.length > 0 && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {formData.habits_and_lifestyle.sports_interest.map((sport, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    padding: '6px 12px',
                    borderRadius: '16px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>{sport}</span>
                  <button
                    onClick={() => handleRemoveSport(index)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#f44336',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '0',
                      lineHeight: '1'
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </Grid>
        )}
      </Grid>
      
      <Grid item xs={12}>
        <TextInput
          label="Personal Description"
          value={formData.habits_and_lifestyle?.description || ""}
          onChange={(e) => handleInputChange("habits_and_lifestyle.description", e.target.value)}
          multiline
          rows={3}
          placeholder="Describe your personality, interests, and lifestyle..."
          fullWidth
        />
      </Grid>
    </Grid>
  );
};

export default FamilyHealthStep;